/**
 * Vertical Context Provider
 * Provides vertical-specific context throughout the app
 */

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { Vertical, VerticalContextValue } from "@/types/vertical";
import { getVerticalByDomain, VERTICAL_CONFIG } from "@/lib/vertical-config";

const VerticalContext = createContext<VerticalContextValue | undefined>(
  undefined,
);

interface VerticalProviderProps {
  children: ReactNode;
  config?: Record<string, Vertical>;
}

export function VerticalProvider({
  children,
  config = VERTICAL_CONFIG,
}: VerticalProviderProps) {
  const [vertical, setVertical] = useState<Vertical>(() => {
    // Detect vertical from hostname or URL param
    const hostname = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);
    const override = urlParams.get('vertical');

    if (override && config[override]) {
      return config[override];
    }
    return getVerticalByDomain(hostname);
  });

  // Update vertical when URL changes (for testing purposes)
  useEffect(() => {
    const handlePopState = () => {
      const hostname = window.location.hostname;
      const urlParams = new URLSearchParams(window.location.search);
      const override = urlParams.get('vertical');

      if (override && config[override]) {
        setVertical(config[override]);
      } else {
        setVertical(getVerticalByDomain(hostname));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [config]);

  const terminology = useMemo(() => vertical.terminology, [vertical]);
  const isVertical = useMemo(
    () => (id: string) => vertical.id === id,
    [vertical],
  );

  const value: VerticalContextValue = useMemo(
    () => ({
      vertical,
      setVertical,
      terminology,
      isVertical,
    }),
    [vertical, terminology, isVertical],
  );

  return (
    <VerticalContext.Provider value={value}>
      {children}
    </VerticalContext.Provider>
  );
}

export function useVertical(): VerticalContextValue {
  const context = useContext(VerticalContext);
  if (!context) {
    throw new Error("useVertical must be used within a VerticalProvider");
  }
  return context;
}

/**
 * Hook for accessing terminology with a translation function
 */
export function useTerminology() {
  const { terminology } = useVertical();

  const t = (key: string, fallback?: string): string => {
    return terminology[key] || fallback || key;
  };

  return { t, terminology };
}
